import React, { useMemo, useState } from 'react';
import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { OverlayScrollArea } from '../components/OverlayScrollArea';
import { CalendarSidebar } from './CalendarSidebar';
import { DayGroup } from './DayGroup';
import { AgendaEmptyState } from './AgendaEmptyState';
import { AgendaSkeleton, AgendaError } from './AgendaStatus';
import { formatDayHeading, parseDateOnly } from './date';
import { countAppointmentsByDate, isNotCanceled, needsChartNote } from './status';
import { APPOINTMENT_TABS } from './types';
import type { AppointmentTab, CalendarAppointment, CalendarMonth } from './types';

export interface AppointmentsCalendarComposer {
    content: React.ReactNode;
    label: React.ReactNode;
    onClose: () => void;
    /** Date the mini calendar should highlight while this composer is open
     *  (e.g. an in-progress draft's date), overriding the day filter. */
    highlightDate?: string;
}

export interface AppointmentsCalendarViewProps {
    appointments: CalendarAppointment[];
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
    todayIso: string;
    /** Hides Add buttons, cancel icons and "Add chart note" pills. Rows with
     *  a note still show the note-chip indicator. */
    readOnly: boolean;
    /** Shows each row's `practiceName`, for a multi-practice consumer. */
    showPractice?: boolean;
    /** Replaces the plain practice-name line on rows when `showPractice` is set. */
    renderPractice?: (appointment: CalendarAppointment) => React.ReactNode;
    renderAvatar: (appointment: CalendarAppointment) => React.ReactNode;
    renderStage?: (appointment: CalendarAppointment) => React.ReactNode;
    /** Host content rendered in the sidebar below the tab chips (e.g. extra filters). */
    sidebarExtra?: React.ReactNode;
    /** Controlled: a host page renders its own search input (e.g. a modal
     *  header) and the view filters by this value. */
    search?: string;
    onSearchChange?: (value: string) => void;
    onSelectAppointment: (appointment: CalendarAppointment) => void;
    /** Row click when `readOnly` is false; falls back to `onSelectAppointment`. */
    onEditAppointment?: (appointment: CalendarAppointment) => void;
    /** Called with the currently selected day (or today) when the sidebar's
     *  or empty state's Add button is used. Ignored when `readOnly`. */
    onAddAppointment?: (prefillDate: string) => void;
    onCancelAppointment?: (appointment: CalendarAppointment) => void;
    onAddChartNote?: (appointment: CalendarAppointment) => void;
    /** Replaces the agenda with an editing pane (e.g. the dashboard's
     *  Composer) while set. */
    composer?: AppointmentsCalendarComposer;
    /** Clamp the mini calendar's month navigation to the fetched data window. */
    minMonth?: CalendarMonth;
    maxMonth?: CalendarMonth;
    /** Fires on every tab switch, e.g. so a host can fetch the past range on demand. */
    onTabChange?: (tab: AppointmentTab) => void;
}

function monthFromIso(dateOnly: string): CalendarMonth {
    const date = parseDateOnly(dateOnly);
    return { year: date.getFullYear(), month: date.getMonth() };
}

function sortKey(appointment: CalendarAppointment): string {
    return appointment.date + (appointment.time || '');
}

export const AppointmentsCalendarView: React.FC<AppointmentsCalendarViewProps> = ({
    appointments,
    isLoading,
    isError,
    onRetry,
    todayIso,
    readOnly,
    showPractice,
    renderPractice,
    renderAvatar,
    renderStage,
    sidebarExtra,
    search = '',
    onSearchChange,
    onSelectAppointment,
    onEditAppointment,
    onAddAppointment,
    onCancelAppointment,
    onAddChartNote,
    composer,
    minMonth,
    maxMonth,
    onTabChange
}) => {
    const [tab, setTabState] = useState<AppointmentTab>('upcoming');
    const [selectedDay, setSelectedDay] = useState<string | null>(null);
    const [month, setMonth] = useState<CalendarMonth>(() => monthFromIso(todayIso));

    const activeAppointments = useMemo(() => appointments.filter(isNotCanceled), [appointments]);
    const counts = useMemo(() => countAppointmentsByDate(activeAppointments), [activeAppointments]);
    const needsNoteCount = useMemo(
        () => activeAppointments.filter((a) => needsChartNote(a, todayIso)).length,
        [activeAppointments, todayIso]
    );

    const filtered = useMemo(() => {
        let list = activeAppointments;
        list = list.filter((a) => {
            if (tab === 'all') return true;
            if (tab === 'needsNote') return needsChartNote(a, todayIso);
            if (tab === 'past') return a.date < todayIso;
            return a.date >= todayIso;
        });
        const trimmedSearch = search.trim().toLowerCase();
        if (trimmedSearch) {
            list = list.filter((a) => a.patientName.toLowerCase().includes(trimmedSearch));
        }
        if (selectedDay) {
            list = list.filter((a) => a.date === selectedDay);
        }
        return [...list].sort((a, b) => {
            const diff = sortKey(a) < sortKey(b) ? -1 : sortKey(a) > sortKey(b) ? 1 : 0;
            // Past and Needs note both read most-recent-first: the visit
            // that happened most recently is the most urgent to document.
            return tab === 'past' || tab === 'needsNote' ? -diff : diff;
        });
    }, [activeAppointments, tab, search, selectedDay, todayIso]);

    const groups = useMemo(() => {
        const map = new Map<string, CalendarAppointment[]>();
        filtered.forEach((a) => {
            const existing = map.get(a.date);
            if (existing) {
                existing.push(a);
            } else {
                map.set(a.date, [a]);
            }
        });
        return [...map.entries()];
    }, [filtered]);

    const filtersOn = !!search.trim() || !!selectedDay || tab !== 'upcoming';

    const setTab = (next: AppointmentTab) => {
        setTabState(next);
        onTabChange?.(next);
    };

    const handleSelectDay = (day: string | null) => {
        setSelectedDay(day);
        if (day) {
            setMonth(monthFromIso(day));
            if (day < todayIso && tab === 'upcoming') setTab('all');
        }
    };

    const handleResetFilters = () => {
        onSearchChange?.('');
        setSelectedDay(null);
        setTab('upcoming');
    };

    const handleSelectRow = (appointment: CalendarAppointment) => {
        (readOnly ? onSelectAppointment : (onEditAppointment ?? onSelectAppointment))(appointment);
    };

    const contextLabel = composer ? (
        <span className="font-medium text-gray-800">{composer.label}</span>
    ) : selectedDay ? (
        <>
            {formatDayHeading(selectedDay, todayIso)} · {filtered.length} appointment
            {filtered.length !== 1 ? 's' : ''}
        </>
    ) : (
        <>
            {APPOINTMENT_TABS.find(([key]) => key === tab)?.[1]} · {filtered.length} appointment
            {filtered.length !== 1 ? 's' : ''}
        </>
    );

    return (
        <div className="flex-1 flex min-h-0">
            <CalendarSidebar
                month={month}
                onMonthChange={setMonth}
                counts={counts}
                selectedDay={composer ? (composer.highlightDate ?? null) : selectedDay}
                onSelectDay={handleSelectDay}
                todayIso={todayIso}
                tab={tab}
                onTabChange={(next) => {
                    setTab(next);
                    setSelectedDay(null);
                }}
                needsNoteCount={needsNoteCount}
                disabled={!!composer}
                readOnly={readOnly}
                onAddAppointment={
                    onAddAppointment ? () => onAddAppointment(selectedDay || todayIso) : undefined
                }
                extra={sidebarExtra}
                minMonth={minMonth}
                maxMonth={maxMonth}
            />

            <div className="flex-1 min-w-0 flex flex-col bg-gray-50/60">
                <div className="flex items-center justify-between gap-3 px-5 h-[46px] border-b border-gray-200 bg-white shrink-0">
                    <div className="text-[13px] text-gray-500">{contextLabel}</div>
                    {composer ? (
                        <button
                            type="button"
                            onClick={composer.onClose}
                            className="flex items-center gap-1.5 text-[12.5px] text-gray-500 hover:text-gray-900 shrink-0"
                        >
                            <ArrowLeftIcon className="h-3.5 w-3.5" />
                            Back to appointments
                        </button>
                    ) : selectedDay ? (
                        <button
                            type="button"
                            onClick={() => setSelectedDay(null)}
                            className="flex items-center gap-1.5 text-[12.5px] text-gray-500 hover:text-gray-900"
                        >
                            Clear day <XMarkIcon className="h-3 w-3" />
                        </button>
                    ) : (
                        filtersOn && (
                            <button
                                type="button"
                                onClick={handleResetFilters}
                                className="text-[12.5px] text-gray-500 hover:text-gray-900"
                            >
                                Reset
                            </button>
                        )
                    )}
                </div>

                {composer ? (
                    composer.content
                ) : (
                    <OverlayScrollArea className="flex-1 min-h-0">
                        {isLoading ? (
                            <AgendaSkeleton />
                        ) : isError ? (
                            <AgendaError onRetry={onRetry} />
                        ) : groups.length === 0 ? (
                            <AgendaEmptyState
                                filtersActive={filtersOn}
                                onClearFilters={handleResetFilters}
                                onAddAppointment={
                                    !readOnly && onAddAppointment
                                        ? () => onAddAppointment(selectedDay || todayIso)
                                        : undefined
                                }
                            />
                        ) : (
                            groups.map(([date, list]) => (
                                <DayGroup
                                    key={date}
                                    date={date}
                                    appointments={list}
                                    todayIso={todayIso}
                                    readOnly={readOnly}
                                    showPractice={showPractice}
                                    renderPractice={renderPractice}
                                    renderAvatar={renderAvatar}
                                    renderStage={renderStage}
                                    onSelect={handleSelectRow}
                                    onCancel={onCancelAppointment}
                                    onAddNote={onAddChartNote}
                                />
                            ))
                        )}
                    </OverlayScrollArea>
                )}
            </div>
        </div>
    );
};

export default AppointmentsCalendarView;
